// Bestellung und Partien in der Akte (E3c, S3 und S4) – die drei Karten an
// ihren festen Plätzen (#akte-bestellung, #akte-dachpartie,
// #akte-bodenpartie; „Zu tun“ springt dorthin – die Plätze stehen schon da,
// während die Karten noch laden). Dazu holt die Akte einmal die Lieferanten
// und Partien der Firma und ihre Vorgaben (Mailtexte, Vorlauf) – beides darf,
// wer bestellen und einteilen darf (Leitung, Büro). Jede Karte steht in einer
// eigenen Fehlergrenze: Geht eine nicht, bleiben die anderen und die Akte stehen.

import type { ReactNode } from "react";
import type { AblaufEinstellungen, Akte, PartnerAntwort } from "@happy-energy/kern";
import { Ikon } from "@happy-energy/oberflaeche";
import { Laedt, LadeFehler, useLaden } from "../laden.tsx";
import type { AkteStelle } from "../navigation.ts";
import { BestellungKarte } from "./BestellungKarte.tsx";
import { KartenGrenze, type KartenName } from "./gemeinsam.tsx";
import { BodenpartieKarte, DachpartieKarte } from "./PartieKarten.tsx";
import "./ablauf.css";

/** Ein Platz: die feste Stelle, an die „Zu tun“ springt – darin die Karte, sobald alles geladen ist. */
function Platz({ stelle, hier, wo, children }: { stelle: AkteStelle; hier: boolean; wo: KartenName; children: ReactNode }) {
  return (
    <div id={`akte-${stelle}`} className={`ablauf-platz ablauf-karte${hier ? " hier" : ""}`} role="region" aria-label={wo}>
      <KartenGrenze wo={wo}>{children}</KartenGrenze>
    </div>
  );
}

export function AblaufKarten({ akte, stelle, neuLaden }: { akte: Akte; stelle: AkteStelle | undefined; neuLaden: () => void }) {
  const partner = useLaden<PartnerAntwort>("/api/crm/partner");
  const einstellungen = useLaden<AblaufEinstellungen>("/api/crm/firma/ablauf");
  const fehler = partner.fehler ?? einstellungen.fehler;
  const p = partner.daten?.partner;
  const e = einstellungen.daten;
  const laedt = fehler === undefined ? <Laedt text="Lieferanten und Partien werden geladen …" /> : null;

  return (
    <section className="crm-karte" aria-label="Bestellung und Partien">
      <div className="karten-kopf">
        <Ikon name="paket" />
        <h2>Bestellung und Partien</h2>
      </div>
      <p className="leise klein akte-leer ablauf-satz">
        Nach dem Freigeben, sobald der Termin steht – in beliebiger Reihenfolge. Je Schritt: antippen, die Mail ansehen, öffnen und abschicken, dann abhaken.
      </p>
      {fehler !== undefined && (
        <LadeFehler
          fehler={fehler}
          nochmal={() => {
            partner.neu();
            einstellungen.neu();
          }}
        />
      )}
      <div className="ablauf-raster">
        <Platz stelle="bestellung" hier={stelle === "bestellung"} wo="Bestellung">
          {p && e ? <BestellungKarte akte={akte} partner={p} einstellungen={e} neuLaden={neuLaden} /> : laedt}
        </Platz>
        <Platz stelle="dachpartie" hier={stelle === "dachpartie"} wo="Dachpartie">
          {p && e ? <DachpartieKarte akte={akte} partner={p} einstellungen={e} neuLaden={neuLaden} /> : laedt}
        </Platz>
        <Platz stelle="bodenpartie" hier={stelle === "bodenpartie"} wo="Bodenpartie">
          {p ? <BodenpartieKarte akte={akte} partner={p} neuLaden={neuLaden} /> : laedt}
        </Platz>
      </div>
    </section>
  );
}
